import { useCallback, useEffect, useRef, useState } from "react";

export const NOTIFICATIONS_FLOATING_BUTTON_SIZE = 34;
export const NOTIFICATIONS_FLOATING_BUTTON_BOTTOM = 100;
export const NOTIFICATIONS_FLOATING_BUTTON_RIGHT = 20;
export const NOTIFICATIONS_PANEL_GAP_FROM_BUTTON = 4;
export const NOTIFICATIONS_PANEL_RIGHT_MOBILE = 14;

const VIEWPORT_MARGIN = 8;
const DRAG_THRESHOLD_PX = 5;

export const getDefaultFabPosition = () => ({
  bottom: NOTIFICATIONS_FLOATING_BUTTON_BOTTOM,
  right: NOTIFICATIONS_FLOATING_BUTTON_RIGHT,
});

export const clampFabPosition = ({ bottom, right }) => {
  const maxBottom =
    window.innerHeight - NOTIFICATIONS_FLOATING_BUTTON_SIZE - VIEWPORT_MARGIN;
  const maxRight =
    window.innerWidth - NOTIFICATIONS_FLOATING_BUTTON_SIZE - VIEWPORT_MARGIN;

  return {
    bottom: Math.min(Math.max(VIEWPORT_MARGIN, bottom), maxBottom),
    right: Math.min(Math.max(VIEWPORT_MARGIN, right), maxRight),
  };
};

export const getFabButtonRect = ({ bottom, right }) => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const left = viewportWidth - right - NOTIFICATIONS_FLOATING_BUTTON_SIZE;
  const top = viewportHeight - bottom - NOTIFICATIONS_FLOATING_BUTTON_SIZE;

  return {
    top,
    left,
    right: viewportWidth - right,
    bottom: viewportHeight - bottom,
    width: NOTIFICATIONS_FLOATING_BUTTON_SIZE,
    height: NOTIFICATIONS_FLOATING_BUTTON_SIZE,
  };
};

const applyFabPositionToElement = ({ element, bottom, right }) => {
  if (!element) {
    return;
  }

  element.style.bottom = `${bottom}px`;
  element.style.right = `${right}px`;
};

/**
 * Draggable fixed position for the notifications tasks button (session-only state).
 * Updates the DOM directly while dragging for smooth movement; commits React state on release.
 */
export const useNotificationsFabPosition = ({
  buttonRef,
  onClick = () => {},
} = {}) => {
  const [fabPosition, setFabPosition] = useState(getDefaultFabPosition);
  const [isDragging, setIsDragging] = useState(false);
  const fabPositionRef = useRef(fabPosition);
  const dragStateRef = useRef(null);
  const onClickRef = useRef(onClick);

  useEffect(() => {
    fabPositionRef.current = fabPosition;
  }, [fabPosition]);

  useEffect(() => {
    onClickRef.current = onClick;
  }, [onClick]);

  const onPointerDown = useCallback((event) => {
    if (event.button !== 0) {
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);

    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      initialBottom: fabPositionRef.current.bottom,
      initialRight: fabPositionRef.current.right,
      hasDragged: false,
    };
  }, []);

  const onPointerMove = useCallback(
    (event) => {
      const dragState = dragStateRef.current;
      if (!dragState || dragState.pointerId !== event.pointerId) {
        return;
      }

      const deltaX = event.clientX - dragState.startX;
      const deltaY = event.clientY - dragState.startY;

      if (!dragState.hasDragged) {
        const distance = Math.hypot(deltaX, deltaY);
        if (distance < DRAG_THRESHOLD_PX) {
          return;
        }

        dragState.hasDragged = true;
        setIsDragging(true);
        if (buttonRef?.current) {
          buttonRef.current.style.cursor = "grabbing";
        }
      }

      event.preventDefault();

      const nextPosition = clampFabPosition({
        right: dragState.initialRight - deltaX,
        bottom: dragState.initialBottom - deltaY,
      });

      fabPositionRef.current = nextPosition;
      applyFabPositionToElement({
        element: buttonRef?.current,
        bottom: nextPosition.bottom,
        right: nextPosition.right,
      });
    },
    [buttonRef]
  );

  const onPointerUp = useCallback(
    (event) => {
      const dragState = dragStateRef.current;
      if (!dragState || dragState.pointerId !== event.pointerId) {
        return;
      }

      const { hasDragged } = dragState;
      dragStateRef.current = null;

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      if (buttonRef?.current) {
        buttonRef.current.style.cursor = "grab";
      }

      if (hasDragged) {
        setIsDragging(false);
        setFabPosition(fabPositionRef.current);
        return;
      }

      setIsDragging(false);
      onClickRef.current();
    },
    [buttonRef]
  );

  useEffect(() => {
    const onResize = () => {
      setFabPosition((current) => {
        const nextPosition = clampFabPosition(current);
        fabPositionRef.current = nextPosition;
        applyFabPositionToElement({
          element: buttonRef?.current,
          bottom: nextPosition.bottom,
          right: nextPosition.right,
        });
        return nextPosition;
      });
    };

    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [buttonRef]);

  return {
    fabPosition,
    isDragging,
    dragHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
    },
  };
};
